import { WidgetProps } from '@rjsf/utils';
import UploadIcon from '@mui/icons-material/Upload';
import { PillIconButton } from 'components/button/PillIconButton';
import useFileManagement from 'api/file-management/useFileManagement';
import { useCallback, useState, useMemo } from 'react';
import { FileManagementRequest } from 'api/model';
import { innerDownloadLink } from 'api/file-management/requests';
import FileManagmentUploadFileDialog from 'views/file-management/FileManagmentUploadFileDialog';
import { useParams } from 'react-router';
import ListSelectAutocomplete from './ListSelectAutocomplete';
import FileRenderer from './CustomRenderers/FileRenderer';
import { FormBlockContext } from './FormBlock';

type FileManagementWidgetOptions = {
  accept?: string;
  enumOptions?: { label: string; value: string }[];
};

const FileManagementWidget = function (props: WidgetProps) {
  const { projectId } = useParams();
  const contextFiles = (props.formContext as FormBlockContext | undefined)?.files;
  const { upload, files: fetchedFiles } = useFileManagement(projectId, true, undefined, !contextFiles);
  const files = contextFiles ?? fetchedFiles;
  const [open, setOpen] = useState(false);
  const widgetOptions = props.options as FileManagementWidgetOptions;

  const filesById = useMemo(() => new Map((files ?? []).map((file) => [file.id, file])), [files]);
  const extractFileId = (value: string) => value?.split('/').pop() ?? value;

  const handleSubmit = useCallback(
    async (formData: FileManagementRequest) => {
      const results = await upload.mutateAsync(formData);
      props.onChange(innerDownloadLink(results.id));
      return results;
    },
    [upload, props],
  );

  const options = useMemo(
    () =>
      (widgetOptions.enumOptions || [])
        .filter((option) => option.value)
        .map((option) => ({
          value: option.label,
          const: option.value,
        })),
    [JSON.stringify(widgetOptions.enumOptions)],
  );

  return (
    <div className="flex items-center gap-1.5">
      <ListSelectAutocomplete
        label={props.label || props.schema?.title || 'File'}
        options={options}
        value={props.value as string}
        onChange={(value) => props.onChange(value)}
        disabled={props.disabled}
        required={props.required}
        render={(value) => (
          <FileRenderer
            file={filesById.get(extractFileId(value))}
            fallback={options.find((o) => o.const === value)?.value ?? value}
          />
        )}
      />
      <PillIconButton
        className="ms-1"
        aria-label="upload file"
        color="info"
        title="Upload"
        onClick={() => setOpen(true)}
        disabled={props.disabled}
      >
        <UploadIcon fontSize="inherit" />
      </PillIconButton>
      <FileManagmentUploadFileDialog
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        open={open}
        formId={`file-management-form-widget-${props.name}`}
        accept={widgetOptions.accept}
      />
    </div>
  );
};

export { FileManagementWidget };
